
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';
import { VatTuService } from '@/core/services/VatTu.service';
import { formatQuantity } from '@/core/helpers/number-format';
import {
  getErrorMsg,
  swalNotification,
} from '@/core/helpers/utils';
import { IVatTuResponseBase } from '@/core/interfaces/ApiResponses';
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from '@/core/helpers/breadcrumb';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import { useLoading } from '@/core/hooks/use-loading';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog.vue';
import ResponsiveLayout from '@/layout/_shared/ResponsiveLayout.vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageContentSection from '@/layout/_shared/PageContentSection.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import ConfirmationApproveDialog from '@/components/confirmation-approve-dialog/ConfirmationApproveDialog.vue';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { VAT_TU } from '@/core/constants/permissions';
import Can from '@/components/Can.vue';
import usePermission from '../../../../core/hooks/usePermission';

export default defineComponent({
  components: {
    Can,
    LabelCustom,
    ConfirmationApproveDialog,
    PageContentBase,
    PageContentSection,
    ButtonCustom,
    ConfirmationDialog,
    ResponsiveLayout,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isCreate = ref(true);
    const { push } = useRouterCustom();
    const { startLoading, endLoading } = useLoading();
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();

    const { canDuyetVatTu } = usePermission();

    const isInaccessible = computed(() => {
      const routeName = route.name;

      if (routeName === 'cap-nhat-vat-tu')
        return process.env.VUE_APP_IS_EDIT_VAT_TU_ENABLE === 'false';

      return process.env.VUE_APP_IS_ADD_VAT_TU_ENABLE === 'false';
    });

    const showApproveVatTuDialog = ref(false);

    const vatTuDetail = ref<IVatTuResponseBase | null>(null);

    const hasZeroReserve = computed(() => {
      const d = vatTuDetail.value;
      if (!d) return false;
      const min = Number(d.muc_du_tru_toi_thieu);
      const max = Number(d.muc_du_tru_toi_da);
      return min === 0 || max === 0;
    });

    const fetchedSupplyDetail = async () => {
      startLoading();
      try {
        const {
          data: { data },
        } = await VatTuService.fetchSupplyDetail(+route.params.id);

        vatTuDetail.value = data;
        document.title = `${vatTuDetail.value.ten_vat_tu} (${vatTuDetail.value.ma_vat_tu}) - Vật tư - Đường sắt Hà Nội`;
        endLoading();
      } catch (e) {
        endLoading();
        await swalNotification(
          getErrorMsg(
            e,
            'Xin lỗi, có vẻ có lỗi xảy khi lấy thông tin vật tư, mời bạn thực hiện lại'
          ),
          'error'
        );
        await router.push({ path: '/trang-chu' });
      }
    };

    onMounted(async () => {
      setCurrentPageTitle('Chi tiết vật tư');

      setBreadcrumbLinks([
        {
          text: 'Vật tư',
          routeData: {
            name: 'supplies',
          },
        },
        {
          text: 'Chi tiết',
        },
      ]);

      await fetchedSupplyDetail();
    });

    return {
      VAT_TU,
      fetchedSupplyDetail,
      ButtonTypeColors,
      ButtonsType,
      canDuyetVatTu,
      showApproveVatTuDialog,
      formatQuantity,
      formatDate: computed(() => moment),
      hasZeroReserve,
      isInaccessible,
      isCreate,
      push,
      vatTuDetail,
    };
  },

  data() {
    return {
      isDeleteVatTuDialogDisplay: false,
    };
  },

  methods: {
    onDeleteVatTuClicked() {
      this.isDeleteVatTuDialogDisplay = true;
    },

    async confirmDelete() {
      this.isDeleteVatTuDialogDisplay = false;

      try {
        await VatTuService.delete(this.vatTuDetail.id);
        await swalNotification('Xóa vật tư thành công', 'success');
        await this.push('/quan-ly/vat-tu/danh-sach');
      } catch {
        await swalNotification(
          'Có lỗi xảy ra, không thể xóa vật tư này',
          'error'
        );
      }
    },
  },
});
